<template>
  <!-- Only show sidebar for users with admin roles (global or company-scoped) -->
  <template v-if="isSidebarVisible">
    <aside
      class="sidenav navbar navbar-vertical navbar-expand-xs border-0 fixed-start bg-white"
      id="sidenav-main"
    >
      <div class="sidenav-header" style="margin-top: 100px">
        <div class="ms-3">
          <CurrentCompany v-if="showsCompanySelector" />
        </div>
      </div>

      <div
        class="collapse navbar-collapse w-auto"
        id="sidenav-collapse-main"
        v-if="keyUserTypes"
      >
        <ul class="navbar-nav">
          <template v-for="(item, index) in navItems" :key="index">
            <!-- Divider -->
            <hr
              v-if="item.type === 'divider'"
              class="horizontal dark mt-2 mb-2"
            />

            <!-- Standard nav link -->
            <li
              v-else-if="item.type === 'link' && item.visible"
              class="nav-item"
            >
              <router-link :to="item.path" :class="navLinkClasses(item)">
                <template v-slot:default>
                  <div
                    class="text-center me-2 d-flex align-items-center justify-content-center"
                  >
                    <i class="material-icons text-dark-blue">{{ item.icon }}</i>
                  </div>
                  <span class="nav-link-text ms-1">{{ item.label }}</span>
                </template>
              </router-link>
            </li>

            <!-- Collapsible section -->
            <li
              v-else-if="item.type === 'collapsible' && item.visible"
              class="nav-item"
            >
              <a
                class="nav-link text-dark-blue"
                data-bs-toggle="collapse"
                :href="'#' + item.collapseId"
                role="button"
                aria-expanded="false"
                :aria-controls="item.collapseId"
              >
                <div
                  class="text-center me-2 d-flex align-items-center justify-content-center"
                >
                  <i class="material-icons text-dark-blue">{{ item.icon }}</i>
                </div>
                <span class="nav-link-text ms-1">{{ item.label }}</span>
              </a>
              <div class="collapse" :id="item.collapseId">
                <ul class="nav">
                  <li
                    v-for="(child, ci) in item.children"
                    :key="ci"
                    v-show="child.visible !== false"
                    class="nav-item ms-2"
                  >
                    <router-link
                      :to="child.path"
                      :class="navLinkClasses(child)"
                    >
                      <template v-slot:default>
                        <div
                          class="text-center me-2 d-flex align-items-center justify-content-center"
                        >
                          <i class="material-icons text-dark-blue">{{
                            child.icon
                          }}</i>
                        </div>
                        <span class="nav-link-text ms-1">{{
                          child.label
                        }}</span>
                      </template>
                    </router-link>
                  </li>
                </ul>
              </div>
            </li>
          </template>
        </ul>
      </div>
    </aside>
  </template>
</template>

<script>
import { usePermissions } from "@/composables/usePermissions";
import CurrentCompany from "@/components/CurrentCompany.vue";
import { computed } from "vue";
import { useRoute } from "vue-router";

export default {
  name: "Sidebar",
  components: {
    CurrentCompany,
  },
  setup() {
    const route = useRoute();

    const {
      isAdmin,
      isCompanyAdmin,
      isProjectAdmin,
      isKeyAccount,
      isQuestionnaireCreator,
      isTranslationManager,
      isTranslator,
      isOnlyParticipant,
      isParticipant,
      keyUserTypes,
      hasPermission,
    } = usePermissions();

    const isCompaniesActive = computed(() => {
      return (
        route.path === "/companies" ||
        route.name === "companyEdit" ||
        route.name === "companyCreate" ||
        route.path.startsWith("/company/")
      );
    });

    // `isTranslator` nije prava rola nego "ima bar jednu delegaciju prevoda", a
    // delegacija je dodatak postojecoj ulozi - ne zamena za nju. Zato se izbor
    // kompanije sakriva samo onome ko je iskljucivo prevodilac. Company admin
    // koji usput prevodi i dalje mora da vidi svoju kompaniju.
    const showsCompanySelector = computed(() => {
      return (
        !isTranslator.value ||
        isAdmin.value ||
        (keyUserTypes.value && keyUserTypes.value.length > 0)
      );
    });

    const isSidebarVisible = computed(() => {
      return (
        isAdmin.value ||
        ((isCompanyAdmin.value ||
          isProjectAdmin.value ||
          isKeyAccount.value ||
          isQuestionnaireCreator.value ||
          isTranslationManager.value) &&
          keyUserTypes.value &&
          keyUserTypes.value.length > 0)
      );
    });

    const navItems = computed(() => [
      {
        type: "link",
        path: "/dashboard",
        icon: "dashboard",
        label: "Dashboard",
        visible: isKeyAccount.value || isCompanyAdmin.value || isAdmin.value,
      },
      {
        type: "link",
        path: "/projects",
        icon: "table_view",
        label:
          isCompanyAdmin.value && keyUserTypes.value?.length === 1
            ? "Projects overview"
            : "Projects",
        visible: isProjectAdmin.value || isCompanyAdmin.value,
      },
      {
        type: "link",
        path: "/keyUsers",
        icon: "supervised_user_circle",
        label: "Key users",
        visible: isCompanyAdmin.value || isAdmin.value,
      },
      {
        type: "link",
        path: "/users",
        icon: "person",
        label: "Participants",
        visible: isProjectAdmin.value,
      },
      {
        type: "link",
        path: "/reports",
        icon: "print",
        label: "Reports",
        visible: isProjectAdmin.value,
      },
      {
        type: "link",
        path: "/downloads",
        icon: "download",
        label: "Downloads",
        visible: isProjectAdmin.value,
      },
      {
        type: "link",
        path: "/mindcloud",
        icon: "cloud",
        label: "MindCloud",
        visible: hasPermission("VIEW_MINDCLOUD"),
      },
      { type: "divider" },
      {
        type: "collapsible",
        icon: "emoji_objects",
        label: "Conceptual area",
        collapseId: "conceptualArea",
        visible: isQuestionnaireCreator.value,
        children: [
          { path: "/profiles", icon: "person", label: "Profiles" },
          {
            path: "/questionnaires",
            icon: "format_list_bulleted",
            label: "Questionnaires",
          },
        ],
      },
      {
        type: "collapsible",
        icon: "translate",
        label: "Translation platform",
        collapseId: "translationPlatform",
        visible: isTranslationManager.value || isAdmin.value,
        children: [
          {
            path: "/translation/overview",
            icon: "bar_chart",
            label: "Overview",
          },
          {
            path: "/translation/groups",
            icon: "folder",
            label: "Translation groups",
            visible: isAdmin.value || isTranslationManager.value,
          },
          {
            path: "/translation/list",
            icon: "translate",
            label: "Translations",
          },
          {
            path: "/translation/delegations",
            icon: "assignment_ind",
            label: "Delegations",
            visible: isAdmin.value,
          },
        ],
      },
      {
        type: "link",
        path: "/companies",
        icon: "store",
        label: "Companies",
        visible: isAdmin.value || isCompanyAdmin.value,
        activeCheck: isCompaniesActive.value,
      },
      {
        type: "link",
        path: "/questionnaire-companies",
        icon: "assignment",
        label: "Questionnaire Companies",
        visible: isAdmin.value,
      },
      {
        type: "link",
        path: "/home",
        icon: "task",
        label: "Tasks",
        visible: true,
      },
      {
        type: "link",
        path: "/admin/logs",
        icon: "manage_search",
        label: "Logs",
        visible: isAdmin.value,
      },
    ]);

    /**
     * Returns dynamic classes for a nav link.
     * Uses item.activeCheck if provided, otherwise falls back to path matching.
     */
    const navLinkClasses = (item) => {
      const isActive =
        item.activeCheck !== undefined
          ? item.activeCheck
          : route.path === item.path;
      return {
        "nav-link": true,
        "text-dark-blue": !isActive,
        active: isActive,
        "bg-gradient-dark": isActive,
      };
    };

    return {
      isAdmin,
      isCompanyAdmin,
      isProjectAdmin,
      isKeyAccount,
      isQuestionnaireCreator,
      isTranslationManager,
      isTranslator,
      showsCompanySelector,
      isOnlyParticipant,
      isParticipant,
      keyUserTypes,
      isCompaniesActive,
      isSidebarVisible,
      navItems,
      navLinkClasses,
    };
  },
};
</script>

<style scoped>
.sidenav {
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.05) !important;
  background: #fbfdff !important;
}

.navbar-nav {
  padding: 0 12px;
}

.nav-item {
  margin-bottom: 4px;
}

.nav-link {
  border-radius: 8px !important;
  padding: 12px 16px !important;
  transition: all 0.2s ease !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  display: flex !important;
  align-items: center !important;
}

.nav-link:not(.active):hover {
  background: #eff6ff !important;
  transform: translateX(4px);
}

.nav-link.active {
  background: linear-gradient(195deg, #00569d, #00569d) !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
}

.nav-link.active .material-icons,
.nav-link.active .nav-link-text {
  color: #ffffff !important;
}

.nav-link .material-icons {
  font-size: 20px !important;
  transition: all 0.2s ease;
}

.nav-link:hover .material-icons {
  transform: scale(1.1);
}

.nav-link-text {
  color: #475569 !important;
  font-weight: 500 !important;
}

.horizontal.dark {
  background: linear-gradient(
    90deg,
    transparent,
    #94a3b8,
    transparent
  ) !important;
  height: 1px !important;
  opacity: 0.9 !important;
  margin: 6px 0 !important;
  border: none !important;
}

/* Collapse menu styling */
.collapse .nav {
  padding-left: 8px;
}

.collapse .nav-item {
  margin-bottom: 2px;
}

.collapse .nav-link {
  padding: 10px 14px !important;
  font-size: 14px !important;
}

/* Company selector styling */
.sidenav-header {
  padding: 0 12px;
  margin-bottom: 24px;
}
</style>
